import React from 'react';
import ChatWindow from './ChatPopup';
import { useAppStore } from '../../stores/application/useAppStore';
import { useAppHandlers } from '../../services/actions/app/useAppHandlers';
import { useExtensionRequest } from '../../services/apis/extension/useExtensionRequest';
import { useConnectionsActions } from '../../stores/connections/useConnectionsActions';
import { useConnectionsStore } from '../../stores/connections/useConnectionsStore';
import { useConversationsHandlers } from '../../services/actions/conversations/useConversationsHandlers';
import { useConnectionsHandlers } from '../../services/actions/connections/useConnectionsHandlers';
import styled from 'styled-components';
import { useAuthStore } from '../../stores/auth/useAuthStore';

const MESSAGE_SENT_SOURCE = 'connection_profile' as const



const ChatPopups: React.FC = () => {
    const { store: { chatPopups, sidebar } } = useAppStore()
    const { store: { connections } } = useConnectionsStore()
    const { store: { user } } = useAuthStore()

    const isSidebarOpen = sidebar.length > 0
    const sidebarWidth = 328
    const additionalOffset = 7
    const rightOffset = isSidebarOpen ? sidebarWidth + additionalOffset : 10

    const { closeChatHandler, updateConversationHandler } = useAppHandlers()
    const { createOrSendMessage, getMessagesFromConversation } = useExtensionRequest()
    const { setConnections } = useConnectionsActions()
    const { updateLastMessagedAndMessageHandler } = useConnectionsHandlers()
    const { syncConversationWithBackendHandler, createNewConversationHandler } = useConversationsHandlers()

    const updateConversation = (item: any, newMsg: any, conversationId: any, isDelivered?: boolean, updateMessages?: boolean) => ({
        ...item,
        ...(conversationId && { conversationId }),
        ...(updateMessages ? 
            { 
                messages: [
                    ...item.messages,
                    { ...newMsg, isDelivered }
                ]
            } : { 
                messages: [
                    ...item.messages
                ]
            })
    });

    const closeChat = (message: any) => {
        closeChatHandler(message.id)
    };

    const updateConnectionLastMessaged = (connectionId: string, userId: string, dateTimestamp: string) => {
        setConnections(connections.map((connection: any) => {
            if (connection._id === connectionId) {
                return {
                    ...connection,
                    lastMessagedAtArray: [
                        ...(connection.lastMessagedAtArray || []).filter((obj: any) => obj._id !== userId),
                        { _id: userId, lastMessagedAt: dateTimestamp, sent: true }
                    ]
                };
            }
            return connection;
        }));
    };

    const sendMessageToExistingConversation = async (item: any, newMsg: any, dateTimestamp: string, hasTemplate?: boolean) => {
        const sendMessageResult = await createOrSendMessage({
            conversationId: item.conversationId,
            profileId: user.linkedinData.entityUrn,
            participantUrns: [item.profileId],
            text: newMsg.text
        });

        if(sendMessageResult) {
            const messageId =
                (sendMessageResult as any)?.messageId ||
                (sendMessageResult as any)?.result?.messageId ||
                undefined
            updateConversationHandler(updateConversation(item, newMsg, item.conversationId, true, true));
            updateConnectionLastMessaged(item.id, item.userId, dateTimestamp);
            updateLastMessagedAndMessageHandler(item.id, dateTimestamp, newMsg.text);
            const prevMsgs: any[] = Array.isArray(item.messages) ? item.messages.slice(-2) : []
            const recentMessages = [...prevMsgs, newMsg]
                .filter((m: any) => typeof (m?.text ?? '') === 'string' && (m?.text ?? '').trim())
                .map((m: any) => ({ sender: m.isSent ? 'user' : 'peer', text: (m.text ?? '').trim() }))
            syncConversationWithBackendHandler(item.convId, true, true, dateTimestamp, newMsg.text, {
                source: MESSAGE_SENT_SOURCE,
                has_template: !!hasTemplate,
                ...(messageId ? { message_id: messageId } : {}),
            }, recentMessages);
        }
    };

    const createNewConversation = async (item: any, newMsg: any, dateTimestamp: string, hasTemplate?: boolean) => {
        const sendResult = await createOrSendMessage({ 
            conversationId: null,
            profileId: user.linkedinData.entityUrn,
            participantUrns: [item.profileId],
            text: newMsg.text
        });
        const conversationId = (sendResult as any)?.conversationId
        const messageId =
            (sendResult as any)?.messageId ||
            (sendResult as any)?.result?.messageId ||
            undefined

        if(!conversationId) return;

        const newMessages = await getMessagesFromConversation(conversationId);
        const updatedChat = {
            ...item,
            messages: [...item.messages, ...newMessages]
        };

        updateConversationHandler(updateConversation(updatedChat, newMsg, conversationId, true));
        updateConnectionLastMessaged(item.id, item.userId, dateTimestamp);
        updateLastMessagedAndMessageHandler(item.id, dateTimestamp, newMsg.text);

        const connection = connections.find((c: any) => c._id === item.id);
        if (connection) {
            createNewConversationHandler(
                connection.profileId,
                new Date(dateTimestamp).getTime(),
                newMsg.text,
                conversationId,
                {
                    source: MESSAGE_SENT_SOURCE,
                    has_template: !!hasTemplate,
                    ...(messageId ? { message_id: messageId } : {}),
                }
            );
        }
    };

    const sendMessage = async (item: any, text: any, hasTemplate?: boolean) => {
        const dateTimestamp = new Date().toISOString();
        const newMsg = {
            text,
            isSent: true,
            dateTimestamp,
        };

        updateConversationHandler(updateConversation(item, newMsg, undefined, false, true));

        if (item.messages.length !== 0) {
            await sendMessageToExistingConversation(item, newMsg, dateTimestamp, hasTemplate);
        } else {
            await createNewConversation(item, newMsg, dateTimestamp, hasTemplate);
        }
    };

return (
    <Wrapper rightOffset={rightOffset}>
        {chatPopups.map((item: any) => (
            <ChatWindow
                key={item.id}
                id={item.id}
                conversationId={item.conversationId}
                userId={item.userId}
                sender={item.sender}
                profilePicture={item.sender.profilePicture}
                messages={item.messages || []}
                prefilledMessage={item.prefilledMessage}
                onSendMessage={(msg: string, hasTemplate?: boolean) => sendMessage(item, msg, hasTemplate)}
                onClose={() => closeChat(item)}
                connection={item.connection}
            />
        ))}
    </Wrapper>
  );
};

interface WrapperProps {
    rightOffset: number;
}

const Wrapper = styled.div<WrapperProps>`
    position: fixed;
    bottom: 0;
    right: 0;
    display: flex;
    align-items: flex-end;
    gap: 16px;
    z-index: 10000000;
    padding: 0 ${({ rightOffset }) => rightOffset}px 0 0;
    pointer-events: none;
`

export default ChatPopups; 